import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, BedDouble, Calendar, FileText, Sparkles, Coffee, MapPin } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../lib/LanguageContext";

const TYPE_ICONS = {
    room: BedDouble,
    facility: Coffee,
    blog: FileText,
    addon: Sparkles,
    page: MapPin,
};

const TYPE_LABELS = {
    al: { room: "Dhomë", facility: "Lehtësira", blog: "Blog", addon: "Shërbim", page: "Faqe" },
    en: { room: "Room", facility: "Facility", blog: "Blog", addon: "Service", page: "Page" },
    it: { room: "Camera", facility: "Servizio", blog: "Blog", addon: "Servizio", page: "Pagina" },
    de: { room: "Zimmer", facility: "Service", blog: "Blog", addon: "Service", page: "Seite" },
};

const PLACEHOLDERS = {
    al: "Kërko: plazh, parking, restorant...",
    en: "Search: beach, parking, restaurant...",
    it: "Cerca: spiaggia, parcheggio, ristorante...",
    de: "Suchen: Strand, Parkplatz, Restaurant...",
};

export default function SearchBar({ buttonClass = "", iconOnly = false, useDarkText = false }) {
    const { lang } = useLang();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const debounceRef = useRef(null);

    const labels = TYPE_LABELS[lang] || TYPE_LABELS.en;
    const placeholder = PLACEHOLDERS[lang] || PLACEHOLDERS.en;

    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus(), 100);
        } else {
            setQ("");
            setResults([]);
        }
    }, [open]);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const term = q.trim();
        if (term.length < 2) {
            setResults([]);
            return;
        }
        setLoading(true);
        debounceRef.current = setTimeout(() => {
            api.get(`/search?q=${encodeURIComponent(term)}&lang=${lang}`)
                .then((r) => setResults(r.data?.results || []))
                .catch(() => setResults([]))
                .finally(() => setLoading(false));
        }, 350);
        return () => clearTimeout(debounceRef.current);
    }, [q, lang]);

    useEffect(() => {
        if (!open) return;
        const onEsc = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, [open]);

    const selectResult = (r) => {
        setOpen(false);
        navigate(r.url);
    };

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                data-testid="search-toggle"
                aria-label="Search"
                className={`flex items-center gap-2 transition-colors ${
                    useDarkText
                        ? "text-[#0F3A59] hover:text-[#CBA871]"
                        : "text-white hover:text-[#CBA871]"
                } ${buttonClass}`}
            >
                <Search size={16} strokeWidth={1.7} />
                {!iconOnly && <span className="text-xs uppercase tracking-wider">{lang === "al" ? "Kërko" : "Search"}</span>}
            </button>

            {open && (
                <div
                    data-testid="search-modal"
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-start justify-center pt-[10vh] px-4"
                    onClick={() => setOpen(false)}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center gap-3 px-5 py-4 border-b border-[#E5E0D8]">
                            <Search size={20} strokeWidth={1.7} className="text-[#0F3A59]/60" />
                            <input
                                ref={inputRef}
                                type="text"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder={placeholder}
                                data-testid="search-input"
                                className="flex-1 outline-none text-base text-[#1C1C1C] placeholder:text-[#1C1C1C]/40 font-light"
                            />
                            <button
                                onClick={() => setOpen(false)}
                                data-testid="search-close"
                                className="text-[#1C1C1C]/50 hover:text-[#1C1C1C] p-1"
                                aria-label="Close"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="max-h-[60vh] overflow-y-auto">
                            {q.trim().length < 2 ? (
                                <div className="p-10 text-center">
                                    <Search size={28} className="mx-auto text-[#1C1C1C]/30 mb-3" />
                                    <p className="text-sm text-[#1C1C1C]/55 font-light">
                                        {lang === "al" ? "Shkruani të paktën 2 karaktere për të kërkuar." : "Type at least 2 characters to search."}
                                    </p>
                                    <div className="flex flex-wrap gap-2 justify-center mt-4">
                                        {["plazh", "parking", "restorant", "wifi"].map((t) => (
                                            <button
                                                key={t}
                                                onClick={() => setQ(t)}
                                                className="text-xs px-3 py-1 rounded-full bg-[#F4EFE6] text-[#0F3A59] hover:bg-[#CBA871] hover:text-white transition-colors"
                                            >
                                                {t}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ) : loading ? (
                                <div className="p-10 text-center text-sm text-[#1C1C1C]/55 font-light">
                                    {lang === "al" ? "Duke kërkuar..." : "Searching..."}
                                </div>
                            ) : results.length === 0 ? (
                                <div className="p-10 text-center">
                                    <p className="text-sm text-[#1C1C1C]/55 font-light">
                                        {lang === "al" ? "Asnjë rezultat për " : "No results for "}
                                        <strong>"{q}"</strong>
                                    </p>
                                </div>
                            ) : (
                                <ul data-testid="search-results">
                                    {results.map((r, i) => {
                                        const Icon = TYPE_ICONS[r.type] || MapPin;
                                        return (
                                            <li key={`${r.url}-${i}`}>
                                                <button
                                                    onClick={() => selectResult(r)}
                                                    data-testid={`search-result-${i}`}
                                                    className="w-full text-left px-5 py-3 hover:bg-[#F4EFE6]/60 border-b border-[#E5E0D8]/40 flex items-start gap-3 transition-colors"
                                                >
                                                    <div className="w-9 h-9 rounded-full bg-[#F4EFE6] flex items-center justify-center shrink-0 mt-0.5">
                                                        <Icon size={15} className="text-[#0F3A59]" strokeWidth={1.6} />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-medium text-[#0F3A59] text-sm">{r.title}</span>
                                                            <span className="text-[10px] uppercase tracking-wider text-[#CBA871] font-medium">
                                                                {labels[r.type] || r.type}
                                                            </span>
                                                        </div>
                                                        {r.snippet && (
                                                            <p className="text-xs text-[#1C1C1C]/60 font-light mt-1 line-clamp-2">
                                                                {r.snippet}
                                                            </p>
                                                        )}
                                                    </div>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>

                        <div className="px-5 py-3 border-t border-[#E5E0D8]/60 bg-[#FDFBF7] text-[10px] text-[#1C1C1C]/50 flex items-center justify-between font-light">
                            <span>{lang === "al" ? "Shtyp ESC për të dalë" : "Press ESC to close"}</span>
                            <span>{results.length > 0 && `${results.length} ${lang === "al" ? "rezultate" : "results"}`}</span>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
